import { action, makeAutoObservable, reaction } from "mobx";
import { UpsertServiceActiveStep } from "./Service.enum";
import { 
  BenefitsModel, 
  ItemDetailInputModel,
  PriceBenefitsInputModel,
  PriceBenefitsValueMappingModel,
  PriceProgramAndLevelsMappingModel,
  PricingPageInputModel, 
  RelatedSectionModel,
  ServiceAndMembershipOptionsPageModel,
} from "../../shared/models";
import { AuthService, PricingPageService, RelatedItemsService } from "../../shared/services";
import { IAPICreateUpdateServiceModel, IAPIGetServiceForEditResponseModel, IAPIItemOptionsRequest } from "../../shared/interfaces";

export default class ServiceStore {
  public headerCaption: string = 'Detail';
  public activeStep: UpsertServiceActiveStep = UpsertServiceActiveStep.Detail;
  public pricingPageData: PricingPageService = new PricingPageService();
  public optionsPage: ServiceAndMembershipOptionsPageModel;
  public showAddBenefitModal: boolean = false;
  public realtedItemService: RelatedItemsService;
  public detailPage: ItemDetailInputModel;
  isSaveUpdateInProgress: boolean = false;
  constructor() {
      this.detailPage = new ItemDetailInputModel();
      this.optionsPage = new ServiceAndMembershipOptionsPageModel();
      this.realtedItemService = new RelatedItemsService();
      makeAutoObservable(this);
      reaction(
          () => this.activeStep,
          () => this.updateStateBasedOnActiveStatusChange(),
      );
      reaction(
          () => this.pricingPageData.prices,
          () => {
              this.updateOptionsPageOnPriceOrBenefitsChange();
          },
      );
      reaction(
          () => this.optionsPage,
          () => {
              this.updateOptionsPageOnPriceOrBenefitsChange();
          },
      );
  }

  updateOptionsPage(data: ServiceAndMembershipOptionsPageModel) {
      this.optionsPage = new ServiceAndMembershipOptionsPageModel(data);
  }

  updateDetailsPage(data: ItemDetailInputModel) {
      this.detailPage = new ItemDetailInputModel(data);
  }

  updateOptionsPageOnPriceOrBenefitsChange() {
      const benefitInputs: PriceBenefitsInputModel[] = [];

      this.pricingPageData.prices.forEach((p) => {
          const allowTermBenefits = p.billingType === 'Term' || p.billingType === 'TermPlusRecurring';
          const allowRecurringBenefits = p.billingType === 'Recurring' || p.billingType === 'TermPlusRecurring';
          const matchedBenefit = this.optionsPage.benefits.find((x) => x.priceReferenceId === p.referenceId);
          if (matchedBenefit) {
              matchedBenefit.termBenefits = allowTermBenefits
                  ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, matchedBenefit.termBenefits)
                  : [];
              matchedBenefit.recurringBenefits = allowRecurringBenefits
                  ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, matchedBenefit.recurringBenefits)
                  : [];

              matchedBenefit.priceName = p.name;
              matchedBenefit.priceBillingType = p.billingType;
              matchedBenefit.isActivePrice = p.isActive;
              if (this.optionsPage.selectedBenefits.length > 0) {
                  benefitInputs.push(matchedBenefit);
              }
          } else {
              if (this.optionsPage.selectedBenefits.length > 0) {
                  benefitInputs.push(
                      new PriceBenefitsInputModel({
                          priceReferenceId: p.referenceId,
                          priceBillingType: p.billingType,
                          isActivePrice: p.isActive,
                          priceName: p.name,
                          termBenefits: allowTermBenefits
                              ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, [])
                              : [],
                          recurringBenefits: allowRecurringBenefits
                              ? this.updateBenefitMapping(this.optionsPage.selectedBenefits, [])
                              : [],
                      }),
                  );
              }
          }
      });

      const programAndLevels: PriceProgramAndLevelsMappingModel[] = [];
      this.pricingPageData.prices.forEach((p) => {
          const matchedPL = this.optionsPage.programAndLevels.find((x) => x.priceReferenceId === p.referenceId);
          if (matchedPL) {
              matchedPL.isActivePrice = p.isActive;
              matchedPL.priceName = p.name;
              programAndLevels.push(matchedPL);
          } else {
              programAndLevels.push(
                  new PriceProgramAndLevelsMappingModel({
                      priceReferenceId: p.referenceId,
                      programId: '',
                      levelId: '',
                      priceName: p.name,
                      isActivePrice: p.isActive,
                  }),
              );
          }
      });
      this.optionsPage.programAndLevels = programAndLevels;
      this.optionsPage.benefits = benefitInputs;
  }

  updateBenefitMapping(benefits: BenefitsModel[], existingBenefits: PriceBenefitsValueMappingModel[]) {
      const mappedBenefits: PriceBenefitsValueMappingModel[] = [];
      benefits.forEach((b) => {
          const existingTermBenefits = existingBenefits.find((x) => x.benefitId === b.id);

          mappedBenefits.push(
              new PriceBenefitsValueMappingModel({
                  benefitId: b.id,
                  benefitName: b.pluralName,
                  value: existingTermBenefits?.value || '',
                  errors: existingTermBenefits?.errors || [],
              }),
          );
      });

      return mappedBenefits;
  }

  validate(step?: UpsertServiceActiveStep): boolean {
      let isValid = true;
      const isTabChange = step ? true : false;
      const stepToValidate = isTabChange ? step : this.activeStep;
      switch (stepToValidate) {
          case UpsertServiceActiveStep.Detail:
              isValid = this.detailPage.validate();
              this.detailPage = new ItemDetailInputModel(this.detailPage);
              break;
          case UpsertServiceActiveStep.Pricing:
              isValid = this.pricingPageData.validate();
              this.pricingPageData.prices = this.pricingPageData.prices.map(
                  (price) => new PricingPageInputModel(price),
              );
              break;
          case UpsertServiceActiveStep.Options:
              isValid = this.optionsPage.validate();
              if (!this.realtedItemService.relatedSectionData.validate()) {
                  isValid = false;
                  this.realtedItemService.relatedSectionData = new RelatedSectionModel({
                      ...this.realtedItemService.relatedSectionData,
                  });
              }
              this.optionsPage = new ServiceAndMembershipOptionsPageModel(this.optionsPage);
              break;
      }
      return isValid;
  }

  @action
  validatePreviousSteps(currentStep: UpsertServiceActiveStep): {
      isValid: boolean;
      errorStep?: UpsertServiceActiveStep;
  } {
      const steps = [
          UpsertServiceActiveStep.Detail,
          UpsertServiceActiveStep.Pricing,
          UpsertServiceActiveStep.Options,
      ];
      const currentStepIndex = steps.indexOf(currentStep);
      for (let i = 0; i < currentStepIndex; i++) {
          const step = steps[i];
          if (!this.validate(step)) {
              return { isValid: false, errorStep: step };
          }
      }
      return { isValid: true };
  }

  updateActiveStep(step: UpsertServiceActiveStep) {
      this.activeStep = step;
  }

  updateStateBasedOnActiveStatusChange() {
      switch (this.activeStep) {
          case UpsertServiceActiveStep.Detail:
              this.headerCaption = 'Service Details';
              break;
          case UpsertServiceActiveStep.Pricing:
              this.headerCaption = 'Service Pricing';
              break;
          case UpsertServiceActiveStep.Options:
              this.headerCaption = 'Service Options';
              break;
      }
  }

  reset() {
      this.headerCaption = 'Detail';
      this.detailPage = new ItemDetailInputModel();
      this.activeStep = UpsertServiceActiveStep.Detail;
      this.pricingPageData = new PricingPageService();
      this.optionsPage = new ServiceAndMembershipOptionsPageModel();
      this.realtedItemService = new RelatedItemsService();
      this.isSaveUpdateInProgress = false;
  }

  toggleAddBenefitModal() {
      this.showAddBenefitModal = !this.showAddBenefitModal;
  }

  createAPIModel(): IAPICreateUpdateServiceModel {
      const usage = this.optionsPage.createAPIModel();
      const relatedItems = this.realtedItemService.relatedSectionData.createAPIModel();
      const programAndLevels = this.optionsPage.createProgramAndLevelsAPIModel();
      const itemDetail = this.detailPage.createAPIModel();
      return {
          ...itemDetail,
          id: '',
          accountId: AuthService.authData.accountId,
          priceCreateData: this.pricingPageData.createAPIModel(),
          options: {
              relatedItems: relatedItems,
              usage: usage,
              programAndLevels: programAndLevels,
          } as IAPIItemOptionsRequest,
          isTrackInventory: false,
      } as IAPICreateUpdateServiceModel;
  }

  constructStoreFromAPI(data: IAPIGetServiceForEditResponseModel) {
      this.detailPage = ItemDetailInputModel.deserializeAPIModel(data);
      if (data.prices && data.prices?.length > 0) {
          this.pricingPageData.prices = PricingPageService.deserializeAPIModel(data.prices || []);
      }

      if (data.options) {
          this.optionsPage = ServiceAndMembershipOptionsPageModel.deserializeAPIModel(data.options);
      }
      if (data.options?.relatedItems) {
          this.realtedItemService.relatedSectionData = RelatedSectionModel.deserializeAPIModel(
              data.options.relatedItems,
          );
      }
  }

  toggleSaveUpdateInProgress() {
      this.isSaveUpdateInProgress = !this.isSaveUpdateInProgress;
  }
}

